import { useState, useEffect, useRef, useCallback } from 'react';
import {
    MessageCircle, Search, Send, Paperclip, Image, File, X, Reply,
    ChevronLeft, MoreVertical, Trash2, Check, CheckCheck, Users, Plus, Phone, Download
} from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';
import {
    getConversations,
    getMessages,
    sendMessage,
    sendMessageWithAttachment,
    markAsRead,
    deleteMessage,
    deleteConversation,
    createConversation,
    getUnreadCount,
    searchParticipantsAndGroups,
    addToMessagingGroup
} from '../../services/messagesService';
import webrtcService from '../../services/webrtcService';
import AudioCall from './AudioCall';
import ConfirmModal from '../ui/ConfirmModal';

/**
 * Messages Component - Full messaging interface like Messenger/WhatsApp
 */
const Messages = () => {
    const { t } = useTranslation();
    const currentUserId = parseInt(localStorage.getItem('visitorPeopleId') || localStorage.getItem('userId') || '0');
    const currentUserName = localStorage.getItem('userName') || '';

    // State
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [replyTo, setReplyTo] = useState(null);
    const [showNewChat, setShowNewChat] = useState(false);
    const [newChatName, setNewChatName] = useState('');
    const [selectedParticipants, setSelectedParticipants] = useState([]); // contacts & peoples
    const [selectedGroups, setSelectedGroups] = useState([]); // groups
    const [peopleSearch, setPeopleSearch] = useState('');
    const [searchResults, setSearchResults] = useState([]); // unified search results
    const [attachments, setAttachments] = useState([]);
    const [showMobileList, setShowMobileList] = useState(true);

    // Audio call state
    const [showCallUI, setShowCallUI] = useState(false);
    const [callType, setCallType] = useState(null); // 'outgoing', 'incoming', 'active'
    const [incomingCaller, setIncomingCaller] = useState(null);

    // Delete confirmation modal
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, conversation: null });

    // Attachment preview modal
    const [previewAttachment, setPreviewAttachment] = useState(null);

    // Refs
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const messageInputRef = useRef(null);

    // Load conversations
    const loadConversations = useCallback(async () => {
        setIsLoading(true);
        const data = await getConversations(searchQuery);
        setConversations(data);
        setIsLoading(false);
        return data;
    }, [searchQuery]);

    // Load messages for selected conversation
    const loadMessages = useCallback(async () => {
        console.log('loadMessages called, selectedConversation:', selectedConversation);
        if (!selectedConversation) return;
        const convId = selectedConversation.id || selectedConversation.ID;
        console.log('loadMessages convId:', convId);
        if (!convId) {
            console.error('No conversation ID found:', selectedConversation);
            return;
        }
        setIsLoading(true);
        const data = await getMessages(convId);
        console.log('loadMessages received data:', data);
        // Messages come in DESC order, reverse for display
        setMessages(data.reverse());
        setIsLoading(false);
        // Mark as read
        await markAsRead(convId);
    }, [selectedConversation]);

    // Initial load and restore selected conversation
    useEffect(() => {
        const loadAndRestore = async () => {
            await loadConversations();
        };
        loadAndRestore();
    }, [loadConversations]);

    // Restore selected conversation from localStorage after conversations are loaded
    useEffect(() => {
        if (conversations.length > 0 && !selectedConversation) {
            const savedConvUuid = localStorage.getItem('selectedConversationUuid');
            if (savedConvUuid) {
                const conv = conversations.find(c =>
                    (c.conversationUuid || c.CONVERSATIONUUID) === savedConvUuid
                );
                if (conv) {
                    setSelectedConversation(conv);
                }
            }
        }
    }, [conversations, selectedConversation]);

    // Save selected conversation to localStorage
    useEffect(() => {
        if (selectedConversation) {
            const uuid = selectedConversation.conversationUuid || selectedConversation.CONVERSATIONUUID;
            if (uuid) {
                localStorage.setItem('selectedConversationUuid', uuid);
            }
        }
    }, [selectedConversation]);

    // Load messages when conversation changes
    useEffect(() => {
        if (selectedConversation) {
            loadMessages();
            setShowMobileList(false);
        }
    }, [selectedConversation, loadMessages]);

    // Scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Search participants and groups for new chat
    useEffect(() => {
        const searchParticipants = async () => {
            if (peopleSearch.length < 2) {
                setSearchResults([]);
                return;
            }
            // Use unified search API (contacts, public peoples, and groups)
            const results = await searchParticipantsAndGroups(peopleSearch);
            setSearchResults(results);
        };
        const debounce = setTimeout(searchParticipants, 300);
        return () => clearTimeout(debounce);
    }, [peopleSearch]);

    // Initialize WebRTC service
    useEffect(() => {
        if (currentUserId && webrtcService) {
            try {
                webrtcService.init(currentUserId, {
                    onIncomingCall: ({ callerId, callerName }) => {
                        setIncomingCaller({ id: callerId, name: callerName });
                        setCallType('incoming');
                        setShowCallUI(true);
                    },
                    onCallAccepted: () => {
                        setCallType('active');
                    },
                    onCallEnded: () => {
                        setShowCallUI(false);
                        setCallType(null);
                        setIncomingCaller(null);
                    },
                    onCallRejected: () => {
                        setShowCallUI(false);
                        setCallType(null);
                    },
                    onError: (error) => {
                        console.error('Call error:', error);
                        setShowCallUI(false);
                        setCallType(null);
                    },
                    onConnectionStateChange: (state) => {
                        if (state === 'connected') {
                            setCallType('active');
                        }
                    }
                });
            } catch (error) {
                console.error('Failed to initialize WebRTC service:', error);
            }
        }

        return () => {
            try {
                webrtcService?.disconnect();
            } catch (error) {
                console.error('Error disconnecting WebRTC:', error);
            }
        };
    }, [currentUserId]);

    // Polling for new messages - DÉSACTIVÉ TEMPORAIREMENT POUR DEBUG
    // useEffect(() => {
    //     const pollInterval = setInterval(() => {
    //         if (selectedConversation) {
    //             loadMessages();
    //         }
    //         loadConversations();
    //     }, 10000); // Poll every 10 seconds

    //     return () => clearInterval(pollInterval);
    // }, [selectedConversation, loadMessages, loadConversations]);

    // Get conversation display name
    const getConversationName = (conv) => {
        return conv.name || conv.NAME || t.messages?.untitled || 'Sans titre';
    };

    // Get participant initials
    const getInitials = (name) => {
        if (!name) return '?';
        const names = name.trim().split(' ');
        if (names.length >= 2) {
            return (names[0][0] + names[names.length - 1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    // Get other participant for direct calls
    const getOtherParticipant = () => {
        if (!selectedConversation || selectedConversation.isGroup || selectedConversation.ISGROUP) return null;
        const participants = selectedConversation.participants || selectedConversation.PARTICIPANTS || [];
        const otherParticipants = participants.filter(p => (p.id || p.ID) !== currentUserId);
        return otherParticipants[0] || null;
    };

    // Start audio call
    const handleStartCall = async () => {
        const otherUser = getOtherParticipant();
        if (!otherUser) {
            console.error('Cannot start call: no other participant');
            return;
        }

        try {
            setCallType('outgoing');
            setShowCallUI(true);
            await webrtcService.startCall(otherUser.id, currentUserName);
        } catch (error) {
            console.error('Failed to start call:', error);
            setShowCallUI(false);
            setCallType(null);
        }
    };

    // Handle call end
    const handleCallEnd = () => {
        setShowCallUI(false);
        setCallType(null);
        setIncomingCaller(null);
    };

    // Handle sending message
    const handleSend = async () => {
        console.log('handleSend called', { newMessage, attachments, selectedConversation, isSending });
        if ((!newMessage.trim() && attachments.length === 0) || !selectedConversation || isSending) {
            console.log('handleSend early return', { hasMessage: !!newMessage.trim(), attachmentsCount: attachments.length, hasConversation: !!selectedConversation, isSending });
            return;
        }

        const convId = selectedConversation.id || selectedConversation.ID;
        if (!convId) {
            console.error('No conversation ID for sending message');
            return;
        }

        setIsSending(true);
        try {
            if (attachments.length > 0) {
                // Send with attachment
                for (const file of attachments) {
                    await sendMessageWithAttachment(
                        convId,
                        newMessage,
                        file,
                        replyTo?.id || replyTo?.ID
                    );
                }
            } else {
                // Send text only
                await sendMessage(
                    convId,
                    newMessage,
                    'text',
                    replyTo?.id || replyTo?.ID
                );
            }

            setNewMessage('');
            setAttachments([]);
            setReplyTo(null);
            await loadMessages();
            await loadConversations();
        } catch (error) {
            console.error('Failed to send message:', error);
        }
        setIsSending(false);
    };

    // Handle key press
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    // Handle file selection
    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files || []);
        setAttachments(prev => [...prev, ...files]);
    };

    // Handle paste (for images)
    const handlePaste = (e) => {
        const items = e.clipboardData?.items;
        if (!items) return;

        for (const item of items) {
            if (item.type.startsWith('image/')) {
                const file = item.getAsFile();
                if (file) {
                    setAttachments(prev => [...prev, file]);
                }
            }
        }
    };

    // Toggle participant/group selection based on type
    const toggleSelection = (item) => {
        const itemId = item.id || item.ID;
        const itemType = item.type || 'contact';

        if (itemType === 'group') {
            // Handle group selection
            setSelectedGroups(prev => {
                const exists = prev.find(g => g.id === itemId);
                if (exists) {
                    return prev.filter(g => g.id !== itemId);
                }
                return [...prev, item];
            });
        } else {
            // Check if user can contact this person (skip if removing)
            const exists = selectedParticipants.find(p => (p.id || p.ID) === itemId);
            if (!exists && item.canContact === false) {
                // Cannot add - show reason (already handled in UI)
                return;
            }
            // Handle contact or people selection
            setSelectedParticipants(prev => {
                if (exists) {
                    return prev.filter(p => (p.id || p.ID) !== itemId);
                }
                return [...prev, item];
            });
        }
        // Clear search input after selection
        setPeopleSearch('');
        setSearchResults([]);
    };

    // Legacy function for compatibility
    const toggleParticipant = toggleSelection;

    // Create new conversation
    const handleCreateConversation = async () => {
        if (!newChatName.trim() || (selectedParticipants.length === 0 && selectedGroups.length === 0)) return;

        // Separate peoples (need to be added to Messagerie group) from contacts
        const peopleParticipants = selectedParticipants.filter(p => p.type === 'people');
        const contactParticipants = selectedParticipants.filter(p => p.type !== 'people');

        // Add peoples to Messagerie group first
        for (const people of peopleParticipants) {
            await addToMessagingGroup(people.id);
        }

        // Build participant IDs (contacts + peoples)
        const participantIds = selectedParticipants.map(p => p.id || p.ID).join(',');

        // Build group IDs
        const groupIds = selectedGroups.map(g => g.id).join(',');

        // Determine if this is a group conversation
        const totalParticipants = selectedParticipants.length + selectedGroups.reduce((acc, g) => acc + (g.memberCount || 0), 0);
        const isGroup = totalParticipants > 1 || selectedGroups.length > 0;

        const result = await createConversation(participantIds, newChatName.trim(), isGroup, groupIds);
        console.log('createConversation result:', result);

        if (result.success) {
            setShowNewChat(false);
            setNewChatName('');
            setSelectedParticipants([]);
            setSelectedGroups([]);
            setPeopleSearch('');
            await loadConversations();
            // Find and select the conversation
            const convs = await getConversations();
            const convId = result.conversationId;
            const conv = convs.find(c => (c.id || c.ID) === convId);
            if (conv) {
                setSelectedConversation(conv);
            }
        } else {
            console.error('Failed to create conversation:', result.message);
        }
    };

    // Delete message
    const handleDeleteMessage = async (msg) => {
        const msgId = msg.id || msg.ID;
        if (!msgId) return;

        if (window.confirm(t.messages?.confirmDelete || 'Delete this message?')) {
            const result = await deleteMessage(msgId);
            if (result.success) {
                await loadMessages();
            }
        }
    };

    // Open delete conversation modal
    const handleDeleteConversation = (e, conversation) => {
        e.stopPropagation(); // Prevent selecting the conversation
        setDeleteModal({ isOpen: true, conversation });
    };

    // Confirm delete conversation
    const confirmDeleteConversation = async () => {
        if (!deleteModal.conversation) return;

        const conversationUuid = deleteModal.conversation.conversationUuid || deleteModal.conversation.CONVERSATIONUUID;

        if (!conversationUuid) {
            console.error('No conversation UUID found', deleteModal.conversation);
            setDeleteModal({ isOpen: false, conversation: null });
            return;
        }

        const result = await deleteConversation(conversationUuid);

        if (result.success) {
            // Check if the deleted conversation was selected
            const selectedUuid = selectedConversation?.conversationUuid || selectedConversation?.CONVERSATIONUUID;
            const wasSelectedDeleted = selectedUuid === conversationUuid;

            // Reload conversations
            const updatedConversations = await loadConversations();

            if (wasSelectedDeleted) {
                // Select the first remaining conversation, or clear if none
                if (updatedConversations && updatedConversations.length > 0) {
                    setSelectedConversation(updatedConversations[0]);
                } else {
                    setSelectedConversation(null);
                    setMessages([]);
                }
                setShowMobileList(true);
            }
        }
        setDeleteModal({ isOpen: false, conversation: null });
    };

    // Format time - full date format DD/MM/YYYY HH:MM
    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
    };

    // Format relative time (il y a x heures/jours/mois/années)
    const formatRelativeTime = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = now - date;
        const diffSeconds = Math.floor(diffMs / 1000);
        const diffMinutes = Math.floor(diffSeconds / 60);
        const diffHours = Math.floor(diffMinutes / 60);
        const diffDays = Math.floor(diffHours / 24);
        const diffMonths = Math.floor(diffDays / 30);
        const diffYears = Math.floor(diffDays / 365);

        if (diffYears > 0) {
            return `${t.messages?.ago || 'il y a'} ${diffYears} ${diffYears === 1 ? (t.messages?.year || 'an') : (t.messages?.years || 'ans')}`;
        } else if (diffMonths > 0) {
            return `${t.messages?.ago || 'il y a'} ${diffMonths} ${t.messages?.months || 'mois'}`;
        } else if (diffDays > 0) {
            return `${t.messages?.ago || 'il y a'} ${diffDays} ${diffDays === 1 ? (t.messages?.day || 'jour') : (t.messages?.days || 'jours')}`;
        } else if (diffHours > 0) {
            return `${t.messages?.ago || 'il y a'} ${diffHours} ${diffHours === 1 ? (t.messages?.hour || 'heure') : (t.messages?.hours || 'heures')}`;
        } else if (diffMinutes > 0) {
            return `${t.messages?.ago || 'il y a'} ${diffMinutes} ${diffMinutes === 1 ? (t.messages?.minute || 'minute') : (t.messages?.minutes || 'minutes')}`;
        } else {
            return t.messages?.justNow || "à l'instant";
        }
    };

    // Render attachment preview
    const renderAttachmentPreview = (attachment, index) => {
        const isImage = attachment.type?.startsWith('image/');
        return (
            <div key={index} className="relative inline-block mr-2 mb-2">
                {isImage ? (
                    <img
                        src={URL.createObjectURL(attachment)}
                        alt=""
                        className="w-16 h-16 object-cover rounded-lg"
                    />
                ) : (
                    <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                        <File className="w-6 h-6 text-gray-500" />
                    </div>
                )}
                <button
                    onClick={() => setAttachments(prev => prev.filter((_, i) => i !== index))}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                >
                    <X className="w-3 h-3" />
                </button>
            </div>
        );
    };

    // Open attachment preview modal
    const handleOpenPreview = (attachment) => {
        setPreviewAttachment(attachment);
    };

    // Download attachment
    const handleDownloadAttachment = (attachment) => {
        const url = attachment.fullUrl;
        window.open(url, '_blank');
    };

    // Check if attachment is an image
    const isImageAttachment = (attachment) => {
        const fileType = attachment.fileType || attachment.FILETYPE || attachment.file_type || '';
        const fileName = attachment.fileName || attachment.FILENAME || attachment.file_name || '';
        // Check by mime type or file extension
        if (fileType.startsWith('image/')) return true;
        const ext = fileName.split('.').pop()?.toLowerCase();
        return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
    };

    // Render message attachment
    const renderMessageAttachment = (attachment) => {
        const isImage = isImageAttachment(attachment);
        const url = attachment.fullUrl;

        if (isImage) {
            return (
                <div
                    onClick={() => handleOpenPreview(attachment)}
                    className="block cursor-pointer"
                >
                    <img
                        src={url}
                        alt={attachment.fileName}
                        className="max-w-[200px] sm:max-w-xs rounded-lg hover:opacity-90"
                    />
                </div>
            );
        }

        return (
            <button
                onClick={() => handleOpenPreview(attachment)}
                className="flex items-center gap-2 p-2 bg-white/10 rounded-lg hover:bg-white/20"
            >
                <File className="w-5 h-5" />
                <span className="text-sm truncate max-w-[200px]">{attachment.fileName}</span>
            </button>
        );
    };

    return (
        <div className="h-full flex bg-white rounded-xl shadow-sm overflow-hidden">
            {/* Conversations List */}
            <div className={`w-full md:w-96 border-r border-gray-200 flex flex-col ${!showMobileList && 'hidden md:flex'}`}>
                {/* Header */}
                <div className="p-4 border-b border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-gray-800">
                            {t.messages?.title || 'Messages'}
                        </h2>
                        <button
                            onClick={() => setShowNewChat(true)}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            title={t.messages?.newChat || 'New conversation'}
                        >
                            <Plus className="w-5 h-5 text-linkoo-purple" />
                        </button>
                    </div>
                    {/* Search */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={t.messages?.searchPlaceholder || 'Search conversations...'}
                            className="w-full pl-9 pr-4 py-2 bg-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-linkoo-purple/50"
                        />
                    </div>
                </div>

                {/* Conversations */}
                <div className="flex-1 overflow-y-auto">
                    {isLoading && conversations.length === 0 ? (
                        <div className="p-4 text-center text-gray-500">
                            {t.common?.loading || 'Loading...'}
                        </div>
                    ) : conversations.length === 0 ? (
                        <div className="p-4 text-center text-gray-500">
                            <MessageCircle className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                            <p>{t.messages?.noConversations || 'No conversations yet'}</p>
                            <button
                                onClick={() => setShowNewChat(true)}
                                className="mt-2 text-linkoo-purple hover:underline"
                            >
                                {t.messages?.startFirst || 'Start your first conversation'}
                            </button>
                        </div>
                    ) : (
                        conversations.map(conv => (
                            <div
                                key={conv.conversationUuid || conv.CONVERSATIONUUID}
                                onClick={() => setSelectedConversation(conv)}
                                className={`w-full p-3 flex items-center gap-3 hover:bg-gray-50 transition-colors cursor-pointer group ${
                                    (selectedConversation?.conversationUuid || selectedConversation?.CONVERSATIONUUID) === (conv.conversationUuid || conv.CONVERSATIONUUID) ? 'bg-linkoo-purple/5' : ''
                                }`}
                            >
                                {/* Avatar */}
                                <div className="relative">
                                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-linkoo-purple to-linkoo-pink flex items-center justify-center text-white font-semibold">
                                        {(conv.isGroup || conv.ISGROUP) ? (
                                            <Users className="w-5 h-5" />
                                        ) : (
                                            getInitials(getConversationName(conv))
                                        )}
                                    </div>
                                    {(conv.unreadCount || conv.UNREADCOUNT) > 0 && (
                                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                                            {(conv.unreadCount || conv.UNREADCOUNT) > 9 ? '9+' : (conv.unreadCount || conv.UNREADCOUNT)}
                                        </span>
                                    )}
                                </div>
                                {/* Info */}
                                <div className="flex-1 min-w-0 text-left">
                                    <div className="flex items-center justify-between">
                                        <span className={`font-medium truncate ${(conv.unreadCount || conv.UNREADCOUNT) > 0 ? 'text-gray-900' : 'text-gray-700'}`}>
                                            {getConversationName(conv)}
                                        </span>
                                        <span className="text-xs text-gray-500 ml-2 whitespace-nowrap">
                                            {formatRelativeTime(conv.displayDate || conv.DISPLAYDATE || conv.lastMessageAt || conv.LASTMESSAGEAT || conv.createdAt || conv.CREATEDAT)}
                                        </span>
                                    </div>
                                    {/* Badges for groups and participants */}
                                    <div className="flex items-center gap-1 mt-1 flex-wrap">
                                        {/* Groups badges (green) */}
                                        {(conv.groups || conv.GROUPS || []).map((group, gIdx) => (
                                            <span
                                                key={`g-${group.groupId || group.GROUPID || gIdx}`}
                                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs"
                                            >
                                                <Users className="w-3 h-3" />
                                                {group.groupName || group.GROUPNAME}
                                            </span>
                                        ))}
                                        {/* Participant badges (purple for contacts, exclude current user) */}
                                        {(() => {
                                            const groups = conv.groups || conv.GROUPS || [];
                                            const groupMemberIds = new Set();
                                            groups.forEach(g => {
                                                const members = g.members || g.MEMBERS || [];
                                                members.forEach(m => groupMemberIds.add(m.id || m.ID));
                                            });
                                            const participants = conv.participants || conv.PARTICIPANTS || [];
                                            return participants
                                                .filter(p => {
                                                    const pId = p.id || p.ID;
                                                    return pId !== currentUserId && !groupMemberIds.has(pId);
                                                })
                                                .map((p, pIdx) => (
                                                    <span
                                                        key={`p-${p.id || p.ID || pIdx}`}
                                                        className="inline-flex items-center px-2 py-0.5 bg-linkoo-purple/10 text-linkoo-purple rounded-full text-xs"
                                                    >
                                                        {p.firstName || p.FIRSTNAME}
                                                    </span>
                                                ));
                                        })()}
                                    </div>
                                    <p className={`text-sm truncate mt-1 ${(conv.unreadCount || conv.UNREADCOUNT) > 0 ? 'text-gray-800 font-medium' : 'text-gray-500'}`}>
                                        {conv.lastMessage || conv.LASTMESSAGE || (t.messages?.noMessages || 'Pas de messages')}
                                    </p>
                                </div>
                                {/* Delete button */}
                                <button
                                    onClick={(e) => handleDeleteConversation(e, conv)}
                                    className="p-2 opacity-0 group-hover:opacity-100 hover:bg-red-100 rounded-full transition-all"
                                    title={t.messages?.deleteConversation || 'Delete conversation'}
                                >
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                </button>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Chat Area */}
            <div className={`flex-1 flex flex-col ${showMobileList && 'hidden md:flex'}`}>
                {!selectedConversation ? (
                    <div className="flex-1 flex items-center justify-center text-gray-500">
                        <div className="text-center">
                            <MessageCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                            <p>{t.messages?.selectConversation || 'Select a conversation to start messaging'}</p>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Chat Header */}
                        <div className="p-4 border-b border-gray-200 flex items-center gap-3">
                            <button
                                onClick={() => setShowMobileList(true)}
                                className="md:hidden p-2 hover:bg-gray-100 rounded-full"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-linkoo-purple to-linkoo-pink flex items-center justify-center text-white font-semibold">
                                {(selectedConversation.isGroup || selectedConversation.ISGROUP) ? (
                                    <Users className="w-4 h-4" />
                                ) : (
                                    getInitials(getConversationName(selectedConversation))
                                )}
                            </div>
                            <div className="flex-1">
                                <h3 className="font-semibold text-gray-800">
                                    {getConversationName(selectedConversation)}
                                </h3>
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <span>{(selectedConversation.participants || selectedConversation.PARTICIPANTS)?.length || 0} {t.messages?.participants || 'participants'}</span>
                                    <span>•</span>
                                    <span>
                                        {formatTime(selectedConversation.displayDate || selectedConversation.DISPLAYDATE || selectedConversation.lastMessageAt || selectedConversation.LASTMESSAGEAT || selectedConversation.createdAt || selectedConversation.CREATEDAT)}
                                        {' '}({formatRelativeTime(selectedConversation.displayDate || selectedConversation.DISPLAYDATE || selectedConversation.lastMessageAt || selectedConversation.LASTMESSAGEAT || selectedConversation.createdAt || selectedConversation.CREATEDAT)})
                                    </span>
                                </div>
                            </div>
                            {/* Audio call button - only for direct conversations */}
                            {!(selectedConversation.isGroup || selectedConversation.ISGROUP) && (
                                <button
                                    onClick={handleStartCall}
                                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                                    title={t.call?.startCall || 'Start audio call'}
                                >
                                    <Phone className="w-5 h-5 text-linkoo-purple" />
                                </button>
                            )}
                        </div>

                        {/* Participants Strip - Me, Groups (green), and individual participants */}
                        {(() => {
                            const groups = selectedConversation.groups || selectedConversation.GROUPS || [];
                            const participants = selectedConversation.participants || selectedConversation.PARTICIPANTS || [];
                            if (groups.length === 0 && participants.length === 0) return null;

                            // Get IDs of all members in groups to filter them out from individual display
                            const groupMemberIds = new Set();
                            groups.forEach(g => {
                                const members = g.members || g.MEMBERS || [];
                                members.forEach(m => groupMemberIds.add(m.id || m.ID));
                            });

                            // Separate current user and other participants not in groups
                            const currentUserParticipant = participants.find(p => (p.id || p.ID) === currentUserId);
                            const otherParticipants = participants.filter(p => {
                                const pId = p.id || p.ID;
                                return pId !== currentUserId && !groupMemberIds.has(pId);
                            });

                            return (
                                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50 overflow-visible">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        {/* Display current user first (pink) */}
                                        {currentUserParticipant && (
                                            <div className="flex flex-col items-center min-w-fit">
                                                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold bg-linkoo-pink">
                                                    {getInitials(currentUserParticipant.fullName || currentUserParticipant.FULLNAME || `${currentUserParticipant.firstName || ''} ${currentUserParticipant.lastName || ''}`)}
                                                </div>
                                                <span className="text-xs text-gray-600 mt-1">{t.messages?.you || 'Moi'}</span>
                                            </div>
                                        )}

                                        {/* Display groups (green like in popup) */}
                                        {groups.map((group, idx) => {
                                            const groupId = group.groupId || group.GROUPID;
                                            const groupName = group.groupName || group.GROUPNAME || 'Groupe';
                                            const memberCount = group.memberCount || group.MEMBERCOUNT || 0;
                                            const members = group.members || group.MEMBERS || [];
                                            return (
                                                <div
                                                    key={`group-${groupId || idx}`}
                                                    className="relative flex items-center gap-2 px-3 py-2 bg-emerald-50 rounded-lg border border-emerald-200 hover:border-emerald-400 transition-colors [&:hover>.group-tooltip]:visible [&:hover>.group-tooltip]:opacity-100"
                                                >
                                                    <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
                                                        <Users className="w-4 h-4 text-white" />
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="text-sm font-medium text-emerald-800">{groupName}</span>
                                                        <span className="text-xs text-emerald-600">{memberCount} {t.messages?.members || 'membres'}</span>
                                                    </div>
                                                    {/* Tooltip with members list on hover */}
                                                    {members.length > 0 && (
                                                        <div className="group-tooltip absolute left-0 top-full mt-2 z-[100] invisible opacity-0 transition-all duration-200 pointer-events-none">
                                                            <div className="bg-white rounded-lg shadow-xl border border-gray-200 p-3 min-w-[200px]">
                                                                <div className="text-xs font-semibold text-gray-500 mb-2">{t.messages?.groupMembers || 'Membres du groupe'}</div>
                                                                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                                                                    {members.map((member, mIdx) => {
                                                                        const mId = member.id || member.ID;
                                                                        const mFirstName = member.firstName || member.FIRSTNAME || '';
                                                                        const mLastName = member.lastName || member.LASTNAME || '';
                                                                        const mFullName = member.fullName || member.FULLNAME || `${mFirstName} ${mLastName}`;
                                                                        const isMemberCurrentUser = mId === currentUserId;
                                                                        return (
                                                                            <div key={mId || mIdx} className="flex items-center gap-2">
                                                                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-semibold ${isMemberCurrentUser ? 'bg-linkoo-pink' : 'bg-emerald-500'}`}>
                                                                                    {getInitials(mFullName)}
                                                                                </div>
                                                                                <span className="text-sm text-gray-700">
                                                                                    {isMemberCurrentUser ? (t.messages?.you || 'Moi') : mFullName}
                                                                                </span>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}

                                        {/* Display individual participants not in any group (purple for contacts) */}
                                        {otherParticipants.map((p, idx) => {
                                            const pId = p.id || p.ID;
                                            const firstName = p.firstName || p.FIRSTNAME || '';
                                            const lastName = p.lastName || p.LASTNAME || '';
                                            const fullName = p.fullName || p.FULLNAME || `${firstName} ${lastName}`;
                                            return (
                                                <div
                                                    key={pId || idx}
                                                    className="flex flex-col items-center min-w-fit"
                                                    title={fullName}
                                                >
                                                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold bg-linkoo-purple">
                                                        {getInitials(fullName)}
                                                    </div>
                                                    <span className="text-xs text-gray-600 mt-1 max-w-[60px] truncate">
                                                        {firstName}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Messages */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-4">
                            {messages.map((msg, index) => {
                                const msgSenderId = msg.senderId || msg.SENDERID;
                                const prevSenderId = messages[index - 1]?.senderId || messages[index - 1]?.SENDERID;
                                const isOwn = msgSenderId === currentUserId;
                                const showAvatar = !isOwn && (index === 0 || prevSenderId !== msgSenderId);

                                return (
                                    <div
                                        key={msg.id || msg.ID}
                                        className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                                    >
                                        <div className={`flex gap-2 max-w-[80%] ${isOwn ? 'flex-row-reverse' : ''}`}>
                                            {/* Avatar */}
                                            {!isOwn && (
                                                <div className={`w-8 h-8 rounded-full bg-linkoo-purple flex items-center justify-center text-xs font-semibold text-white ${!showAvatar && 'invisible'}`}>
                                                    {getInitials(msg.senderName || msg.SENDERNAME)}
                                                </div>
                                            )}

                                            <div>
                                                {/* Sender name for group chats */}
                                                {!isOwn && showAvatar && (selectedConversation.isGroup || selectedConversation.ISGROUP) && (
                                                    <p className="text-xs text-gray-500 mb-1 ml-1">
                                                        {msg.senderName || msg.SENDERNAME}
                                                    </p>
                                                )}

                                                {/* Reply preview */}
                                                {(msg.replyToId || msg.REPLYTOID) && (msg.replyContent || msg.REPLYCONTENT) && (
                                                    <div className={`text-xs p-2 rounded-t-lg border-l-2 ${isOwn ? 'bg-linkoo-pink/30 border-white text-white' : 'bg-linkoo-purple/30 border-white text-white'}`}>
                                                        <span className="font-medium">{msg.replySenderName || msg.REPLYSENDERNAME}</span>
                                                        <p className="truncate opacity-70">{msg.replyContent || msg.REPLYCONTENT}</p>
                                                    </div>
                                                )}

                                                {/* Message bubble */}
                                                <div
                                                    className={`group relative px-4 py-2 rounded-2xl ${
                                                        (msg.replyToId || msg.REPLYTOID) ? 'rounded-t-none' : ''
                                                    } ${
                                                        isOwn
                                                            ? 'bg-linkoo-pink text-white'
                                                            : 'bg-linkoo-purple text-white'
                                                    }`}
                                                >
                                                    {(msg.isDeleted || msg.ISDELETED) ? (
                                                        <span className="italic opacity-70">
                                                            {t.messages?.deleted || 'Message deleted'}
                                                        </span>
                                                    ) : (
                                                        <>
                                                            {/* Attachments */}
                                                            {(msg.attachments || msg.ATTACHMENTS)?.map((att, i) => (
                                                                <div key={i} className="mb-2">
                                                                    {renderMessageAttachment(att)}
                                                                </div>
                                                            ))}

                                                            {/* Text content */}
                                                            {(msg.content || msg.CONTENT) && (
                                                                <p className="whitespace-pre-wrap break-words">
                                                                    {msg.content || msg.CONTENT}
                                                                </p>
                                                            )}

                                                            {/* Actions */}
                                                            <div className={`absolute top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 ${isOwn ? '-left-16' : '-right-16'}`}>
                                                                <button
                                                                    onClick={() => setReplyTo(msg)}
                                                                    className="p-1 hover:bg-gray-200 rounded"
                                                                    title={t.messages?.reply || 'Reply'}
                                                                >
                                                                    <Reply className="w-4 h-4 text-gray-500" />
                                                                </button>
                                                                {isOwn && (
                                                                    <button
                                                                        onClick={() => handleDeleteMessage(msg)}
                                                                        className="p-1 hover:bg-gray-200 rounded"
                                                                        title={t.messages?.delete || 'Delete'}
                                                                    >
                                                                        <Trash2 className="w-4 h-4 text-red-500" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </>
                                                    )}
                                                </div>

                                                {/* Time */}
                                                <p className={`text-xs text-gray-400 mt-1 ${isOwn ? 'text-right' : 'text-left'}`}>
                                                    {formatTime(msg.createdAt || msg.CREATEDAT)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Reply preview */}
                        {replyTo && (
                            <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 flex items-center gap-2">
                                <Reply className="w-4 h-4 text-gray-500" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-linkoo-purple">
                                        {replyTo.senderName || replyTo.SENDERNAME}
                                    </p>
                                    <p className="text-sm text-gray-600 truncate">
                                        {replyTo.content || replyTo.CONTENT}
                                    </p>
                                </div>
                                <button
                                    onClick={() => setReplyTo(null)}
                                    className="p-1 hover:bg-gray-200 rounded"
                                >
                                    <X className="w-4 h-4 text-gray-500" />
                                </button>
                            </div>
                        )}

                        {/* Attachment previews */}
                        {attachments.length > 0 && (
                            <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
                                {attachments.map((file, i) => renderAttachmentPreview(file, i))}
                            </div>
                        )}

                        {/* Input area */}
                        <div className="p-4 border-t border-gray-200">
                            <div className="flex items-end gap-2">
                                {/* Attachment button */}
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                                    title={t.messages?.attach || 'Attach file'}
                                >
                                    <Paperclip className="w-5 h-5 text-gray-500" />
                                </button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                />

                                {/* Message input */}
                                <div className="flex-1">
                                    <textarea
                                        ref={messageInputRef}
                                        value={newMessage}
                                        onChange={(e) => setNewMessage(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        onPaste={handlePaste}
                                        placeholder={t.messages?.typePlaceholder || 'Type a message...'}
                                        rows={1}
                                        className="w-full px-4 py-2 bg-gray-100 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-linkoo-purple/50"
                                        style={{ maxHeight: '120px' }}
                                    />
                                </div>

                                {/* Send button */}
                                <button
                                    onClick={handleSend}
                                    disabled={(!newMessage.trim() && attachments.length === 0) || isSending}
                                    className="p-2 bg-gradient-to-r from-linkoo-purple to-linkoo-pink text-white rounded-full hover:opacity-90 transition-opacity disabled:opacity-50"
                                >
                                    <Send className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* New Chat Modal */}
            {showNewChat && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
                        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                            <h3 className="font-semibold text-gray-800">
                                {t.messages?.newChat || 'Nouvelle conversation'}
                            </h3>
                            <button
                                onClick={() => {
                                    setShowNewChat(false);
                                    setNewChatName('');
                                    setSelectedParticipants([]);
                                    setSelectedGroups([]);
                                    setPeopleSearch('');
                                }}
                                className="p-2 hover:bg-gray-100 rounded-full"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-4 space-y-4">
                            {/* Conversation name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t.messages?.conversationName || 'Nom de la conversation'} *
                                </label>
                                <input
                                    type="text"
                                    value={newChatName}
                                    onChange={(e) => setNewChatName(e.target.value)}
                                    placeholder={t.messages?.conversationNamePlaceholder || 'Ex: Projet X, Famille...'}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-linkoo-purple/50 focus:border-linkoo-purple"
                                    autoFocus
                                />
                            </div>

                            {/* Selected items (participants + groups) */}
                            {(selectedParticipants.length > 0 || selectedGroups.length > 0) && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t.messages?.selectedParticipants || 'Participants sélectionnés'} ({selectedParticipants.length + selectedGroups.length})
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                        {/* Selected groups - Green badges */}
                                        {selectedGroups.map(group => (
                                            <span
                                                key={`group-${group.id}`}
                                                className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm"
                                            >
                                                <Users className="w-3 h-3" />
                                                {group.groupName} ({group.memberCount})
                                                <button
                                                    onClick={() => toggleSelection(group)}
                                                    className="hover:bg-emerald-200 rounded-full p-0.5"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </span>
                                        ))}
                                        {/* Selected participants */}
                                        {selectedParticipants.map(person => {
                                            const isPeople = person.type === 'people';
                                            return (
                                                <span
                                                    key={person.id || person.ID}
                                                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${
                                                        isPeople
                                                            ? 'bg-blue-100 text-blue-700'
                                                            : 'bg-linkoo-purple/10 text-linkoo-purple'
                                                    }`}
                                                >
                                                    {person.firstName} {person.lastName}
                                                    <button
                                                        onClick={() => toggleSelection(person)}
                                                        className={`rounded-full p-0.5 ${
                                                            isPeople ? 'hover:bg-blue-200' : 'hover:bg-linkoo-purple/20'
                                                        }`}
                                                    >
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Search participants and groups */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t.messages?.addParticipantsOrGroups || 'Ajouter des participants ou groupes'} *
                                </label>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="text"
                                        value={peopleSearch}
                                        onChange={(e) => setPeopleSearch(e.target.value)}
                                        placeholder={t.messages?.searchParticipantsGroups || 'Rechercher contacts, personnes ou groupes...'}
                                        className="w-full pl-9 pr-4 py-2 bg-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-linkoo-purple/50"
                                    />
                                </div>
                            </div>

                            {/* Search results */}
                            <div className="max-h-48 overflow-y-auto">
                                {searchResults.length === 0 && peopleSearch.length >= 2 ? (
                                    <p className="text-center text-gray-500 py-4">
                                        {t.messages?.noResults || 'Aucun résultat trouvé'}
                                    </p>
                                ) : searchResults.length === 0 && peopleSearch.length > 0 && peopleSearch.length < 2 ? (
                                    <p className="text-center text-gray-400 py-4 text-sm">
                                        {t.messages?.minChars || 'Tapez au moins 2 caractères'}
                                    </p>
                                ) : (
                                    searchResults.map(item => {
                                        const itemId = item.id;
                                        const itemType = item.type;
                                        const isGroup = itemType === 'group';
                                        const isPeople = itemType === 'people';
                                        const isContact = itemType === 'contact';

                                        // Check contactability (groups are always contactable)
                                        const canContact = isGroup ? true : (item.canContact !== false);

                                        // Check if selected
                                        const isSelected = isGroup
                                            ? selectedGroups.some(g => g.id === itemId)
                                            : selectedParticipants.some(p => p.id === itemId);

                                        // Determine colors based on type and contactability
                                        let bgColor, textColor, iconBg;
                                        if (!canContact) {
                                            // Not contactable - grayed out
                                            bgColor = 'bg-gray-50 cursor-not-allowed';
                                            textColor = 'text-gray-400';
                                            iconBg = 'bg-gray-300';
                                        } else if (isGroup) {
                                            bgColor = isSelected ? 'bg-emerald-100' : 'hover:bg-emerald-50';
                                            textColor = 'text-emerald-700';
                                            iconBg = isSelected ? 'bg-emerald-600' : 'bg-emerald-500';
                                        } else if (isPeople) {
                                            bgColor = isSelected ? 'bg-blue-100' : 'hover:bg-blue-50';
                                            textColor = 'text-blue-700';
                                            iconBg = isSelected ? 'bg-blue-600' : 'bg-blue-500';
                                        } else {
                                            bgColor = isSelected ? 'bg-linkoo-purple/10' : 'hover:bg-gray-50';
                                            textColor = 'text-linkoo-purple';
                                            iconBg = isSelected ? 'bg-linkoo-purple' : 'bg-gradient-to-br from-linkoo-purple to-linkoo-pink';
                                        }

                                        return (
                                            <button
                                                key={`${itemType}-${itemId}`}
                                                onClick={() => canContact && toggleSelection(item)}
                                                disabled={!canContact}
                                                title={!canContact ? (item.contactReason || t.messages?.mustContactFirst || 'Ce contact doit vous contacter en premier') : ''}
                                                className={`w-full p-3 flex items-center gap-3 rounded-lg transition-colors ${bgColor} ${!canContact ? 'opacity-60' : ''}`}
                                            >
                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${iconBg}`}>
                                                    {isSelected ? (
                                                        <Check className="w-5 h-5" />
                                                    ) : isGroup ? (
                                                        <Users className="w-5 h-5" />
                                                    ) : (
                                                        getInitials(`${item.firstName} ${item.lastName}`)
                                                    )}
                                                </div>
                                                <div className="text-left flex-1">
                                                    {isGroup ? (
                                                        <>
                                                            <p className="font-medium text-gray-800">
                                                                {item.groupName}
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                {item.memberCount} {t.messages?.members || 'membres'}
                                                            </p>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <p className={`font-medium ${canContact ? 'text-gray-800' : 'text-gray-400'}`}>
                                                                {item.firstName} {item.lastName}
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                {item.email}
                                                                {isPeople && canContact && (
                                                                    <span className="ml-2 text-blue-500">
                                                                        ({t.messages?.publicProfile || 'Profil public'})
                                                                    </span>
                                                                )}
                                                                {!canContact && (
                                                                    <span className="ml-2 text-orange-500">
                                                                        ({t.messages?.privateProfile || 'Profil privé'})
                                                                    </span>
                                                                )}
                                                            </p>
                                                        </>
                                                    )}
                                                </div>
                                                {isSelected && (
                                                    <span className={`text-xs font-medium ${textColor}`}>
                                                        {t.common?.selected || 'Sélectionné'}
                                                    </span>
                                                )}
                                                {/* Not contactable badge */}
                                                {!canContact && !isSelected && (
                                                    <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-600">
                                                        {t.messages?.mustContactFirst || 'Doit vous contacter'}
                                                    </span>
                                                )}
                                                {/* Type badge */}
                                                {canContact && !isSelected && (
                                                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                                                        isGroup ? 'bg-emerald-100 text-emerald-600' :
                                                        isPeople ? 'bg-blue-100 text-blue-600' :
                                                        'bg-purple-100 text-purple-600'
                                                    }`}>
                                                        {isGroup ? (t.messages?.group || 'Groupe') :
                                                         isPeople ? (t.messages?.public || 'Public') :
                                                         (t.messages?.contact || 'Contact')}
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })
                                )}
                            </div>

                            {/* Create button */}
                            <button
                                onClick={handleCreateConversation}
                                disabled={!newChatName.trim() || (selectedParticipants.length === 0 && selectedGroups.length === 0)}
                                className="w-full py-3 bg-gradient-to-r from-linkoo-purple to-linkoo-pink text-white font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {t.messages?.createConversation || 'Créer la conversation'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Audio Call UI */}
            <AudioCall
                isOpen={showCallUI}
                onClose={() => {
                    setShowCallUI(false);
                    setCallType(null);
                }}
                callType={callType}
                remoteUser={
                    callType === 'incoming'
                        ? incomingCaller
                        : getOtherParticipant()
                            ? {
                                id: getOtherParticipant().id || getOtherParticipant().ID,
                                name: getOtherParticipant().fullName || getOtherParticipant().FULLNAME ||
                                    `${getOtherParticipant().firstName || getOtherParticipant().FIRSTNAME} ${getOtherParticipant().lastName || getOtherParticipant().LASTNAME}`
                            }
                            : null
                }
                onCallEnd={handleCallEnd}
            />

            {/* Delete Conversation Confirmation Modal */}
            {deleteModal.isOpen && deleteModal.conversation && (
                <ConfirmModal
                    isOpen={deleteModal.isOpen}
                    onClose={() => setDeleteModal({ isOpen: false, conversation: null })}
                    onConfirm={confirmDeleteConversation}
                    title={t.messages?.deleteConversation || 'Delete conversation'}
                    message={t.messages?.confirmDeleteConversation || 'Delete this conversation?'}
                    icon={MessageCircle}
                    variant="theme"
                >
                    <div className="bg-gray-100 rounded-lg p-4 flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-linkoo-purple to-linkoo-pink flex items-center justify-center text-white font-semibold">
                            {(deleteModal.conversation.isGroup || deleteModal.conversation.ISGROUP) ? (
                                <Users className="w-5 h-5" />
                            ) : (
                                getInitials(getConversationName(deleteModal.conversation))
                            )}
                        </div>
                        <div>
                            <p className="font-semibold text-gray-900">
                                {getConversationName(deleteModal.conversation)}
                            </p>
                            {(deleteModal.conversation.lastMessage || deleteModal.conversation.LASTMESSAGE) && (
                                <p className="text-sm text-gray-500 truncate max-w-[200px]">
                                    {deleteModal.conversation.lastMessage || deleteModal.conversation.LASTMESSAGE}
                                </p>
                            )}
                        </div>
                    </div>
                </ConfirmModal>
            )}

            {/* Attachment Preview Modal */}
            {previewAttachment && (
                <div
                    className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
                    onClick={() => setPreviewAttachment(null)}
                >
                    <div
                        className="relative max-w-4xl max-h-[90vh] bg-white rounded-xl overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b border-gray-200">
                            <div className="flex items-center gap-3 min-w-0">
                                {isImageAttachment(previewAttachment) ? (
                                    <Image className="w-5 h-5 text-linkoo-purple flex-shrink-0" />
                                ) : (
                                    <File className="w-5 h-5 text-linkoo-purple flex-shrink-0" />
                                )}
                                <span className="font-medium text-gray-800 truncate">
                                    {previewAttachment.fileName || 'Fichier'}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => handleDownloadAttachment(previewAttachment)}
                                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-linkoo-purple to-linkoo-pink text-white rounded-lg hover:opacity-90 transition-opacity"
                                >
                                    <Download className="w-4 h-4" />
                                    <span>{t.common?.download || 'Télécharger'}</span>
                                </button>
                                <button
                                    onClick={() => setPreviewAttachment(null)}
                                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                                >
                                    <X className="w-5 h-5 text-gray-500" />
                                </button>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-4 flex items-center justify-center bg-gray-50 max-h-[calc(90vh-80px)] overflow-auto">
                            {isImageAttachment(previewAttachment) ? (
                                <img
                                    src={previewAttachment.fullUrl}
                                    alt={previewAttachment.fileName || previewAttachment.FILENAME || 'Image'}
                                    className="max-w-full max-h-[70vh] object-contain rounded-lg"
                                />
                            ) : (
                                <div className="flex flex-col items-center gap-4 py-12">
                                    <File className="w-20 h-20 text-gray-400" />
                                    <p className="text-gray-600 text-center">
                                        {previewAttachment.fileName || previewAttachment.FILENAME}
                                    </p>
                                    <p className="text-sm text-gray-400">
                                        {previewAttachment.fileType || previewAttachment.FILETYPE || 'Fichier'}
                                        {(previewAttachment.fileSize || previewAttachment.FILESIZE) && ` - ${Math.round((previewAttachment.fileSize || previewAttachment.FILESIZE) / 1024)} KB`}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Messages;
